import React, { useMemo, useEffect, useState } from 'react';
import { useI18n } from '../../i18n';
import { fetchAvailabilityPreview } from '../../services/AvailabilityService';
import ScheduleAvailabilityCalendarSkeleton from './ScheduleAvailabilityCalendarSkeleton';
import './ScheduleAvailabilityCalendar.css';

const SLOT_HOURS = 2;
const SLOTS_PER_DAY = 12;
const DAYS_COUNT = 5;
const TOTAL_SLOTS = DAYS_COUNT * SLOTS_PER_DAY;

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (value) => {
  const date = toDate(value);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const combineDateAndTime = (dateValue, timeValue) => {
  const date = toDate(dateValue);
  const time = toDate(timeValue);

  if (!date && !time) return null;
  if (!date) return time;
  if (!time) return date;

  const combined = new Date(date);
  combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return combined;
};

const addDays = (dateValue, days) => {
  const date = toDate(dateValue);
  if (!date) return null;
  date.setDate(date.getDate() + days);
  return date;
};

const overlaps = (rangeStart, rangeEnd, busyStart, busyEnd) =>
  rangeStart && rangeEnd && busyStart && busyEnd && rangeStart < busyEnd && rangeEnd > busyStart;

const ScheduleAvailabilityCalendar = ({
  memberIds = [],
  availableUsers = [],
  startDate = null,
  startTime = null,
  endDate = null,
  endTime = null,
}) => {
  const { t } = useI18n();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const gridStartDate = useMemo(
    () => startOfDay(startDate),
    [startDate]
  );

  const selectedStart = useMemo(
    () => combineDateAndTime(startDate, startTime),
    [startDate, startTime]
  );

  const selectedEnd = useMemo(
    () => combineDateAndTime(endDate, endTime),
    [endDate, endTime]
  );

  // Fetch availability data whenever memberIds or startDate changes
  useEffect(() => {
    if (!memberIds || memberIds.length === 0) {
      setMembers([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchAvailabilityPreview(memberIds, gridStartDate);
        setMembers(response?.data?.members || []);
      } catch (error) {
        console.error('Error fetching availability preview:', error);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [memberIds, gridStartDate]);

  const getTimeSlot = (slotIndex) => {
    const hour = (slotIndex % SLOTS_PER_DAY) * SLOT_HOURS;
    return `${String(hour).padStart(2, '0')}:00`;
  };

  const getSlotDateTime = (slotIndex) => {
    if (!gridStartDate) return null;

    const dayOffset = Math.floor(slotIndex / SLOTS_PER_DAY);
    const hour = (slotIndex % SLOTS_PER_DAY) * SLOT_HOURS;
    const slotDate = addDays(gridStartDate, dayOffset);
    slotDate.setHours(hour, 0, 0, 0);
    return slotDate;
  };

  const getDayName = (dayIndex) => {
    const date = addDays(gridStartDate, dayIndex);
    return date?.toLocaleDateString('en-US', { weekday: 'long' }) || '';
  };

  const getSlotIndexForDateTime = (dateTimeValue) => {
    const dateTime = toDate(dateTimeValue);
    if (!dateTime || !gridStartDate) return -1;

    const currentDay = startOfDay(dateTime);
    const dayDiff = Math.floor((currentDay - gridStartDate) / (1000 * 60 * 60 * 24));

    if (dayDiff < 0 || dayDiff >= DAYS_COUNT) {
      return -1;
    }

    const slotHour = Math.floor(dateTime.getHours() / SLOT_HOURS) * SLOT_HOURS;
    return dayDiff * SLOTS_PER_DAY + slotHour / SLOT_HOURS;
  };

  const isUserBusyAtSlot = (member, slotIndex) => {
    if (!member?.busy_times?.length) return false;

    const slotStart = getSlotDateTime(slotIndex);
    if (!slotStart) return false;

    const slotEnd = new Date(slotStart);
    slotEnd.setHours(slotEnd.getHours() + SLOT_HOURS, 0, 0, 0);

    return member.busy_times.some((busyPeriod) =>
      overlaps(slotStart, slotEnd, toDate(busyPeriod.start), toDate(busyPeriod.end))
    );
  };

  const isUserBusyForSelection = (member, computedSelectedSlot) => {
    if (!member?.busy_times?.length || computedSelectedSlot < 0) return false;

    const slotAlignedStart = getSlotDateTime(computedSelectedSlot);
    if (!slotAlignedStart) return false;

    const hasCompleteRange = selectedStart && selectedEnd && selectedEnd > selectedStart;
    const rangeStart = hasCompleteRange ? selectedStart : slotAlignedStart;
    const rangeEnd = hasCompleteRange
      ? selectedEnd
      : new Date(slotAlignedStart.getTime() + SLOT_HOURS * 60 * 60 * 1000);

    return member.busy_times.some((busyPeriod) =>
      overlaps(rangeStart, rangeEnd, toDate(busyPeriod.start), toDate(busyPeriod.end))
    );
  };

  const selectedSlot = useMemo(
    () => getSlotIndexForDateTime(selectedStart),
    [selectedStart, gridStartDate]
  );

  // Empty state: no members selected yet
  if (!memberIds || memberIds.length === 0) {
    return (
      <div className="sac-container">
        <p className="sac-empty-state">{t('pages.calendar.schedule.no_members_selected') || 'No members selected yet'}</p>
      </div>
    );
  }

  if (loading) {
    return <ScheduleAvailabilityCalendarSkeleton />;
  }

  if (!gridStartDate) {
    return (
      <div className="sac-container">
        <p className="sac-loading-state">{t('pages.calendar.schedule.select_start_date') || 'Select a start date'}</p>
      </div>
    );
  }

  if (!members.length) {
    return (
      <div className="sac-container">
        <p className="sac-loading-state">{t('pages.calendar.schedule.no_availability_data') || 'No availability data found'}</p>
      </div>
    );
  }

  return (
    <div className="sac-container">
      <table className="sac-table">
        <thead>
          <tr>
            <th className="sac-members-col" rowSpan="2">
              Members
            </th>
            {[...Array(DAYS_COUNT)].map((_, dayIndex) => (
              <th key={dayIndex} colSpan={SLOTS_PER_DAY}>
                {getDayName(dayIndex)}
              </th>
            ))}
          </tr>
          <tr>
            {[...Array(TOTAL_SLOTS)].map((_, slotIndex) => (
              <th key={slotIndex} className="sac-time-slot">
                {getTimeSlot(slotIndex)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const isBusyAtSelected = isUserBusyForSelection(member, selectedSlot);

            return (
              <tr key={member.id}>
                <td className="sac-members-col">
                  <div className="sac-member-wrapper">
                    <div className="sac-member-name">{member.name}</div>
                  </div>
                </td>
                {[...Array(TOTAL_SLOTS)].map((_, slotIndex) => {
                  const isBusy = isUserBusyAtSlot(member, slotIndex);
                  const isSelectedTime = slotIndex === selectedSlot;

                  let highlightClassName = null;
                  if (isSelectedTime && isBusy) {
                    highlightClassName = 'sac-highlight-busy-selected';
                  } else if (isSelectedTime) {
                    highlightClassName = 'sac-highlight-selected';
                  } else if (isBusy) {
                    highlightClassName = 'sac-highlight-busy';
                  }

                  return (
                    <td
                      key={slotIndex}
                      className={`sac-slot ${isSelectedTime ? 'sac-slot-selected' : ''}`}
                    >
                      {highlightClassName && <div className={`sac-highlight ${highlightClassName}`} />}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ScheduleAvailabilityCalendar;
